import { API_BASE, AUTH_TOKEN } from '../config';

// Ensemble — the conversation a room is having with itself.
//
// Khari, 2026-08-04: "the models are independent from the storyline."
//
// So nothing here knows a model. A placement carries `data.actor` naming a PART;
// the API joins that to the part's lines and hands back a cast script. Swapping
// the body that plays the barber is an edit to one JSON key on one placement —
// no line of dialogue moves, and this file does not change at all.
//
// Reads are public: everyone in the room hears the same conversation, and it has
// to play for a guest who has no avatar. Writing a PART is admin.
const hdr = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${AUTH_TOKEN}` });

/**
 * The scene this room is playing right now.
 *
 * `avatarId` only decides WHICH scene — a room can hold gossip that is only true
 * once you have released something. Absent, you get the unconditional set, which
 * is what a guest and a first-time visitor should hear.
 *
 * Never throws: a quiet room is a fine failure. Pre-migration this returns an
 * empty cast and the salon is exactly as it was yesterday.
 */
export async function loadEnsemble(zone, avatarId = null) {
  try {
    const q = new URLSearchParams(avatarId ? { avatarId: String(avatarId) } : {});
    const res = await fetch(`${API_BASE}/api/world/ensemble/${encodeURIComponent(zone)}?${q}`);
    const d = await res.json().catch(() => ({}));
    return { cast: d.cast || [], scene: d.scene || null, skipped: d.skipped || [] };
  } catch {
    return { cast: [], scene: null, skipped: [] };
  }
}

/**
 * The JOBS that can be worked in this room — the placement panel's picker (admin).
 *
 * A role has no name, no face and no voice: it is duties, tools and house rules.
 * Who is doing it is the placement, because the body standing there IS the
 * person (`ref` plus `data.avatarId`, written together). Falls back to the old
 * parts list so the panel still works with migration 284 pending.
 */
export async function listRoles(zone = null) {
  try {
    const q = new URLSearchParams(zone ? { zone } : {});
    const res = await fetch(`${API_BASE}/api/world/roles?${q}`, { headers: hdr() });
    if (!res.ok) throw new Error(String(res.status));
    const d = await res.json().catch(() => ({}));
    return (d.roles || []).map((r) => ({
      key: r.role_key, role: r.role, title: r.title, tools: r.tools || [], speech: r.speech || [],
    }));
  } catch {
    const actors = await listActors(zone);
    return actors.map((a) => ({ key: a.actorKey, role: a.role, title: a.name || a.actorKey, tools: [], speech: [] }));
  }
}

/** The parts that can be cast in this room — pre-284 fallback for listRoles. */
export async function listActors(zone = null) {
  try {
    const q = new URLSearchParams(zone ? { zone } : {});
    const res = await fetch(`${API_BASE}/api/world/actors?${q}`, { headers: hdr() });
    const d = await res.json().catch(() => ({}));
    return d.actors || [];
  } catch {
    return [];
  }
}

/**
 * Attach avatars to placements made before roles existed (admin).
 *
 * roster.json is the slug→avatar mapping and it lives HERE, on the client, next
 * to the bodies — an entry appears the day an avatar gets a rigged GLB. The
 * server has no copy and should not grow one, so we hand it over on demand
 * rather than pasting a snapshot of it into a migration that would be stale the
 * following week. Only fills blanks; safe to press twice.
 */
export async function syncRoster(npcs = []) {
  const roster = npcs.filter((n) => n.slug && n.avatarId).map((n) => ({ slug: n.slug, avatarId: n.avatarId }));
  const res = await fetch(`${API_BASE}/api/world/roster-sync`, {
    method: 'POST', headers: hdr(), body: JSON.stringify({ roster }),
  });
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(d.error || `sync failed (${res.status})`);
  return d.attached || 0;
}

/** Write a part (admin). Upserts on actorKey. */
export async function saveActor(body) {
  const res = await fetch(`${API_BASE}/api/world/actors`, { method: 'POST', headers: hdr(), body: JSON.stringify(body) });
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(d.error || `save failed (${res.status})`);
  return d.actor;
}

/**
 * One poll for a clip that was still rendering when the scene was handed over.
 * Shares the dialogue poll route — same cache, same hash, one implementation of
 * "is this line spoken yet". Returns a URL or null; never throws.
 */
export async function lineClip(key) {
  return (await lineClipTimed(key)).url;
}

/** The clip and how long it plays (measured from the file by the API): `{ url, secs }`, either may be null. */
export async function lineClipTimed(key) {
  try {
    const res = await fetch(`${API_BASE}/api/dialogue/line-audio/${key}`);
    const d = await res.json().catch(() => ({}));
    return { url: d?.url || null, secs: Number(d?.secs) > 0 ? Number(d.secs) : null };
  } catch {
    return { url: null, secs: null };
  }
}
